import { FC, useEffect, useState } from "react";
import {
    Autocomplete,
    InputAdornment,
    TextField,
    Grid,
    FormGroup,
} from "@mui/material";
import { MuiGenericIcon } from "../../../utils/dynamic-icon";
import { range } from "lodash";
import { DateTime } from "luxon";
import { StepNavigation, StepNavProps } from "../StepNavigation";
import { CardFormRoot } from "./PaymentMethodField";
import { initializeMicroform, resetMicroform } from "../../../cybersource/CyberSourceMicroform"; // Your new file
import { navigate, sendCardData, StepperAction } from "../../../redux/slices/stepper-slice";
import { AppDispatch } from "../../../redux/store";
import { useDispatch } from "react-redux";

// You'll pass this from PaymentMethodField or via context/redux
// For now, assuming it's available via props or context — adjust as needed
type CardFormExtraProps = {
    isMicroform?: boolean;
    captureContext?: string;
};

export const CardForm: FC<StepNavProps & CardFormExtraProps> = ({
    nextLabel,
    backLabel,
    onClose,
    isMicroform = false,
    captureContext,
}) => {
    const dispatch: AppDispatch = useDispatch()
    // State for all fields (used in both modes — Microform only ignores number/cvv)
    const [number, setNumber] = useState('');
    const [name, setName] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');
    const [cvv, setCvv] = useState('');
    const [zipCode, setZipCode] = useState('');

    const navigateForward = () => dispatch(sendCardData({ number, name, month, year, cvv, zipCode }))
    const navigateBack = () => dispatch(navigate(StepperAction.BACK, null, onClose))

    // Initialize Microform when in Microform mode and captureContext is available
    useEffect(() => {
        if (isMicroform && captureContext) {
            initializeMicroform(captureContext).catch(console.error);
        }

        return () => {
            resetMicroform();
        };
    }, [isMicroform, captureContext]);

    return (
        <>
            <CardFormRoot>
                {/* Name on Card - always regular input */}
                <TextField
                    variant="standard"
                    placeholder="Name on Card"
                    name="nameoncard"
                    fullWidth
                    value={name}
                    onChange={e => setName(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <MuiGenericIcon iconName="AccountBox" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Card Number */}
                <Grid item xs={12}>
                    <FormGroup row>
                        {isMicroform ? (
                            <div style={{ width: '100%', position: 'relative' }}>
                                <div
                                    className="microform-field"
                                    id="microform-number-container"
                                />
                                <MuiGenericIcon
                                    iconName="CreditCard"
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#999',
                                        pointerEvents: 'none',
                                        zIndex: 10,
                                        fontSize: 24,
                                    }}
                                />
                            </div>
                        ) : (
                            <TextField
                                variant="standard"
                                placeholder="Card Number"
                                name="cardnumber"
                                fullWidth
                                value={number}
                                onChange={e => setNumber(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MuiGenericIcon iconName="CreditCard" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </FormGroup>
                </Grid>

                {/* Expiry Month & Year */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Autocomplete
                        disablePortal
                        options={['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']}
                        inputValue={month}
                        onInputChange={(_, value) => setMonth(value || '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Exp. MM"
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MuiGenericIcon iconName="CalendarToday" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                        sx={{ flex: 1 }}
                    />
                    <Autocomplete
                        disablePortal
                        options={range(DateTime.now().year, DateTime.now().year + 11).map(y => String(y))}
                        inputValue={year}
                        onInputChange={(_, value) => setYear(value || '')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="YYYY"
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MuiGenericIcon iconName="CalendarToday" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                        sx={{ flex: 1 }}
                    />
                </div>

                {/* CVV & Zip Code */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Grid item xs={6}>
                        <FormGroup row>
                            {isMicroform ? (
                                <div style={{ width: '100%', position: 'relative' }}>
                                    <div
                                        className="microform-field"
                                        id="microform-securityCode-container"
                                    />
                                    <MuiGenericIcon
                                        iconName="Lock"
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#999',
                                            pointerEvents: 'none',
                                            zIndex: 10,
                                            fontSize: 24,
                                        }}
                                    />
                                </div>
                            ) : (
                                <TextField
                                    variant="standard"
                                    placeholder="CVV"
                                    fullWidth
                                    value={cvv}
                                    onChange={e => setCvv(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MuiGenericIcon iconName="Lock" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </FormGroup>
                    </Grid>

                    <Grid item xs={6}>
                        <FormGroup row>
                            <TextField
                                variant="standard"
                                placeholder="Zip Code"
                                fullWidth
                                value={zipCode}
                                onChange={e => setZipCode(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MuiGenericIcon iconName="LocationOn" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormGroup>
                    </Grid>
                </div>
            </CardFormRoot>

            {/* ← ADD THIS BACK */}
            <StepNavigation nextLabel={nextLabel} backLabel={backLabel} navigateForward={navigateForward}
                navigateBack={navigateBack} />
        </>
    );
};